import {
  CellClickedEvent,
  CellKeyDownEvent,
  CellMouseDownEvent,
  CellMouseOverEvent,
  CellRange,
  Events,
  GridApi,
  GridOptions,
} from '@ag-grid-community/core'

/**
 * All select row Behavior handled till date.
 * 1.click to selected single row -Handled by default behavior of grid
 * 2.CRTL+Left Click to select multiple rows one by one -Handled by default behavior of grid
 * 3.left click and drag to select multiple continuous(single range)  rows
 * 4.ctrl+left click and drag to select multiple range of rows
 * 5.shift+arrowUp or arrowDown for selection
 * 6.ctrl+A to select all rows
 * 7.Escape to deselect all rows
 * 8.ctrl+left click on selected row to deselect it - This is handled by default behavior of grid.
 * 9.shift-click to select a range handled by grid default behavior(For example click on 1st than Escape+left click in 10th row then all the row from 1-10 will be selected)
 * PS- After deselecting rows using event.api.clearRangeSelection(), we encountered an issue where all selected cells were deselected. To address this, we manipulated the grid's cellRanges, specifically removing only the deselected row's cells. This ensures that copying after deselecting a row from any selected row range only includes the cells of the remaining selected rows.
 */
export const heliosMultiRowSelectionConfig: GridOptions = {
  rowSelection: 'multiple', //allow multiple rows to be selected
  suppressRowClickSelection: false, //enables click to select, including ctrl and shift modifiers for multiple
  enableRangeSelection: true, //allows ranges (cell blocks) to be copied
  suppressMultiRangeSelection: false, //multi range can be selected at a time
  suppressCopyRowsToClipboard: true, //must manually click ctrl-c to copy
}

/**
 *To add some extra selection behavior over grid default behavior
 */
export function enableHeliosMultiRowDragSelection(api: GridApi) {
  if (localStorage.getItem('helios-use-old-multi-row') === '1') {
    let selectedRowCellRanges: CellRange[] | null = api.getCellRanges()
    api.addEventListener(
      Events.EVENT_CELL_CLICKED,
      (event: CellClickedEvent) => {
        if (
          (event.event as PointerEvent).button == 0 &&
          (event.event as PointerEvent).ctrlKey == true &&
          !event.node.isSelected()
        ) {
          //To achieve point 8here we are clearing cellRanges so that deselected row didn't get selected again if we start selection after deselecting some of rows
          const cellRanges = api.getCellRanges()

          if (cellRanges && event.node.rowIndex != undefined) {
            selectedRowCellRanges = filterAndSplitCellRanges(
              cellRanges,
              event.node.rowIndex,
            )
          }
          api.clearRangeSelection()
          selectedRowCellRanges?.forEach(range => {
            const startRowIndex = range.startRow?.rowIndex ?? null
            const endRowIndex = range.endRow?.rowIndex ?? null

            const rangeToAdd = {
              rowStartIndex: startRowIndex,
              rowEndIndex: endRowIndex,
              columns: range.columns,
            }

            if (startRowIndex !== undefined && endRowIndex !== undefined) {
              api.addCellRange(rangeToAdd)
            }
          })
        }
      },
    )
    api.addEventListener(
      Events.EVENT_CELL_MOUSE_OVER,
      (event: CellMouseOverEvent) => {
        if ((event.event as MouseEvent).buttons === 1) {
          if ((event.event as MouseEvent).ctrlKey === true) {
            selectRowsOld(event.api) //Point 4 implemented here
          } else {
            event.api.deselectAll()
            selectRowsOld(event.api) //Point 3 implemented here
          }
        }
      },
    )
    api.addEventListener(
      Events.EVENT_CELL_KEY_DOWN,
      (event: CellKeyDownEvent) => {
        onKeyBoardActionOnly(event)
      },
    )
  } else {
    let selectedRowCellRanges: CellRange[] | null = api.getCellRanges()
    let dragPosition = { startIndex: -1, endIndex: -1 }
    // Add a mouse down event to capture the initial state
    api.addEventListener(
      Events.EVENT_CELL_MOUSE_DOWN,
      (event: CellMouseDownEvent) => {
        if ((event.event as MouseEvent).ctrlKey == true) {
          dragPosition.startIndex = event.rowIndex ?? -1
        }
      },
    )
    api.addEventListener(
      Events.EVENT_CELL_CLICKED,
      (event: CellClickedEvent) => {
        if (
          (event.event as PointerEvent).button == 0 &&
          (event.event as PointerEvent).ctrlKey == true &&
          !event.node.isSelected()
        ) {
          //To achieve point 8here we are clearing cellRanges so that deselected row didn't get selected again if we start selection after deselecting some of rows
          const cellRanges = api.getCellRanges()

          if (cellRanges && event.node.rowIndex != undefined) {
            selectedRowCellRanges = filterAndSplitCellRanges(
              cellRanges,
              event.node.rowIndex,
            )
          }
          api.clearRangeSelection()
          selectedRowCellRanges?.forEach(range => {
            const startRowIndex = range.startRow?.rowIndex ?? null
            const endRowIndex = range.endRow?.rowIndex ?? null

            const rangeToAdd = {
              rowStartIndex: startRowIndex,
              rowEndIndex: endRowIndex,
              columns: range.columns,
            }

            if (startRowIndex !== undefined && endRowIndex !== undefined) {
              api.addCellRange(rangeToAdd)
            }
          })
        }
      },
    )
    api.addEventListener(
      Events.EVENT_CELL_MOUSE_OVER,
      (event: CellMouseOverEvent) => {
        if ((event.event as MouseEvent).buttons === 1) {
          if ((event.event as MouseEvent).ctrlKey === true) {
            dragPosition.endIndex = event.rowIndex ?? -1
          } else {
            event.api.deselectAll()
            selectRows(event.api) //Point 3 implemented here
          }
        }
      },
    )
    document.addEventListener('mouseup', event => {
      if (
        event.button === 0 &&
        event.ctrlKey === true &&
        dragPosition.startIndex != -1 &&
        dragPosition.endIndex != -1 &&
        dragPosition.startIndex != dragPosition.endIndex
      ) {
        selectRows(api, true) //Point 4 implemented here
        dragPosition = { startIndex: -1, endIndex: -1 }
      }
    })
    api.addEventListener(
      Events.EVENT_CELL_KEY_DOWN,
      (event: CellKeyDownEvent) => {
        onKeyBoardActionOnly(event)
      },
    )
  }
}
/**
 *This function is to handle only key board event like:ctrl+a shift+arrow up or shift+arrow down, Escape
 */
export function onKeyBoardActionOnly(event: CellKeyDownEvent) {
  const ArrowKey = ['ArrowDown', 'ArrowUp']
  if ((event.event as KeyboardEvent).key == 'Escape') {
    event.api.clearRangeSelection()
    event.api.deselectAll() //Point 7 implemented here
  }
  if (
    (event.event as KeyboardEvent).ctrlKey == true &&
    (event.event as KeyboardEvent).key.toLowerCase() == 'a'
  ) {
    localStorage.getItem('helios-use-old-multi-row') === '1'
      ? selectRowsOld(event.api)
      : selectRows(event.api) //Point 6 implemented here
  }
  if (
    (event.event as KeyboardEvent).shiftKey == true &&
    ArrowKey.includes((event.event as KeyboardEvent).key)
  ) {
    localStorage.getItem('helios-use-old-multi-row') === '1'
      ? selectRowsOld(event.api)
      : selectRows(event.api) //Point 5 implemented here
  }
}

/**
 *Common function to select Rows for given CellRanges
 */
export function selectRows(api: GridApi, deselectAllowed = false) {
  const cellRanges = api.getCellRanges()

  if (!cellRanges || cellRanges.length < 1) return
  const activeRange = cellRanges[cellRanges.length - 1]
  let startRow = activeRange.startRow?.rowIndex
  let endRow = activeRange.endRow?.rowIndex
  if (startRow == undefined || endRow == undefined) return
  if (startRow > endRow) {
    const tmp = startRow
    startRow = endRow
    endRow = tmp
  }
  let minimumUnselectedRowIndex = endRow
  let maximumUnselectedRowIndex = startRow

  for (let i = startRow; i <= endRow; i++) {
    const needsToBeDeselected =
      deselectAllowed && api.getDisplayedRowAtIndex(i)?.isSelected()
    api
      .getDisplayedRowAtIndex(i)
      ?.setSelected(!needsToBeDeselected, false, false)
    if (needsToBeDeselected) {
      if (i < minimumUnselectedRowIndex) {
        minimumUnselectedRowIndex = i
      }
      if (i > maximumUnselectedRowIndex) {
        maximumUnselectedRowIndex = i
      }
    }
  }

  // if no unselection happened then return
  if (
    minimumUnselectedRowIndex == endRow &&
    maximumUnselectedRowIndex == startRow
  ) {
    return
  }
  // now split on minimum maximum unselection
  const selectedRowCellRanges = filterAndSplitCellRangesForRangeDeselected(
    cellRanges,
    minimumUnselectedRowIndex,
    maximumUnselectedRowIndex,
  )
  api.clearRangeSelection()
  selectedRowCellRanges?.forEach(range => {
    const startRowIndex = range.startRow?.rowIndex ?? null
    const endRowIndex = range.endRow?.rowIndex ?? null

    const rangeToAdd = {
      rowStartIndex: startRowIndex,
      rowEndIndex: endRowIndex,
      columns: range.columns,
    }

    if (startRowIndex !== undefined && endRowIndex !== undefined) {
      api.addCellRange(rangeToAdd)
    }
  })
}

/**
 * Common function to select Rows for given CellRanges old to be removed
 */
export function selectRowsOld(api: GridApi) {
  const cellRanges = api.getCellRanges()

  if (!cellRanges || cellRanges.length < 1) return
  cellRanges?.forEach(cellRange => {
    let startRow = cellRange.startRow?.rowIndex
    let endRow = cellRange.endRow?.rowIndex
    if (startRow == undefined || endRow == undefined) return
    if (startRow > endRow) {
      const tmp = startRow
      startRow = endRow
      endRow = tmp
    }

    for (let i = startRow; i <= endRow; i++) {
      api.getDisplayedRowAtIndex(i)?.setSelected(true, false, false)
    }
  })
}

/**
 * Filter and Split the cellRange so that the deSelected cellRange is removed from the original cellRange.
 */
export function filterAndSplitCellRanges(
  cellRange: CellRange[],
  deSelectedRowIndex: number,
): CellRange[] {
  const result: CellRange[] = []

  for (const range of cellRange) {
    if (
      range.endRow !== undefined &&
      range.startRow !== undefined &&
      deSelectedRowIndex !== undefined
    ) {
      const { startRow, endRow } = range
      const { rowIndex: startRowIndex } = startRow
      const { rowIndex: endRowIndex } = endRow

      if (
        startRowIndex === deSelectedRowIndex &&
        endRowIndex === deSelectedRowIndex
      ) {
        // Remove the elements where endRow.rowIndex == deSelectedRowIndex && startRow.rowIndex == deSelectedRowIndex
        continue
      }

      // Split the elements where the range contains the deSelectedRowIndex
      const minRowIndex = Math.min(startRowIndex, endRowIndex)
      const maxRowIndex = Math.max(startRowIndex, endRowIndex)
      // If the deSelectedRowIndex is in middle of the range
      if (
        deSelectedRowIndex &&
        minRowIndex < deSelectedRowIndex &&
        maxRowIndex > deSelectedRowIndex
      ) {
        result.push({
          ...range,
          endRow: {
            rowIndex: deSelectedRowIndex - 1,
            rowPinned: endRow.rowPinned,
          },
          startRow: {
            rowIndex: minRowIndex,
            rowPinned: startRow.rowPinned,
          },
        })

        result.push({
          ...range,
          startRow: {
            rowIndex: deSelectedRowIndex + 1,
            rowPinned: startRow.rowPinned,
          },
          endRow: { rowIndex: maxRowIndex, rowPinned: endRow.rowPinned },
        })
      }
      // If deSelectedRowIndex is the start of the range
      else if (startRow.rowIndex === deSelectedRowIndex) {
        result.push({
          ...range,
          startRow: {
            rowIndex: deSelectedRowIndex + 1,
            rowPinned: startRow.rowPinned,
          },
        })
      }
      // If deSelectedRowIndex is the end of the range
      else if (endRow.rowIndex === deSelectedRowIndex) {
        result.push({
          ...range,
          endRow: {
            rowIndex: deSelectedRowIndex - 1,
            rowPinned: endRow.rowPinned,
          },
        })
      }
      // If the range doesn't contains deSelectedRowIndex
      else {
        result.push(range)
      }
    }
  }

  return result
}

/**
 * This function takes a cellRange and deSelectedRowIndexStart and deSelectedRowIndexEnd.
 * It filters out the ranges where the deSelectedRowIndex is in the range and split the range into two if the deSelectedRowIndex is in the middle of the range
 *
 * @param cellRange - The cell range object
 * @param deSelectedRowIndexStart - The start index of the deselected row
 * @param deSelectedRowIndexEnd - The end index of the deselected row
 * @returns - The filtered and split cell range object
 */
export function filterAndSplitCellRangesForRangeDeselected(
  cellRange: CellRange[],
  deSelectedRowIndexStart: number,
  deSelectedRowIndexEnd: number,
): CellRange[] {
  const result: CellRange[] = []

  for (const range of cellRange) {
    if (range.endRow && range.startRow) {
      const { startRow, endRow } = range
      const { rowIndex: startRowIndex } = startRow
      const { rowIndex: endRowIndex } = endRow

      // Split the elements where the range contains the deSelectedRowIndex
      const minRowIndex = Math.min(startRowIndex, endRowIndex)
      const maxRowIndex = Math.max(startRowIndex, endRowIndex)

      if (
        minRowIndex === deSelectedRowIndexStart &&
        maxRowIndex === deSelectedRowIndexEnd
      ) {
        // Remove the elements where endRow.rowIndex == deSelectedRowIndex && startRow.rowIndex == deSelectedRowIndex
        continue
      }

      // If the deSelectedRowIndex is in middle of the range
      if (
        minRowIndex < deSelectedRowIndexStart &&
        maxRowIndex > deSelectedRowIndexEnd
      ) {
        result.push({
          ...range,
          endRow: {
            rowIndex: deSelectedRowIndexStart - 1,
            rowPinned: endRow.rowPinned,
          },
          startRow: {
            rowIndex: minRowIndex,
            rowPinned: startRow.rowPinned,
          },
        })

        result.push({
          ...range,
          startRow: {
            rowIndex: deSelectedRowIndexEnd + 1,
            rowPinned: startRow.rowPinned,
          },
          endRow: { rowIndex: maxRowIndex, rowPinned: endRow.rowPinned },
        })
      }
      // If deSelectedRowIndex is the start of the range
      else if (
        minRowIndex <= deSelectedRowIndexStart &&
        maxRowIndex >= deSelectedRowIndexStart
      ) {
        result.push({
          ...range,
          endRow: {
            rowIndex: deSelectedRowIndexStart - 1,
            rowPinned: endRow.rowPinned,
          },
          startRow: {
            rowIndex: minRowIndex,
            rowPinned: startRow.rowPinned,
          },
        })
      }
      // If deSelectedRowIndex is the end of the range
      else if (
        maxRowIndex >= deSelectedRowIndexEnd &&
        minRowIndex <= deSelectedRowIndexEnd
      ) {
        result.push({
          ...range,
          startRow: {
            rowIndex: deSelectedRowIndexEnd + 1,
            rowPinned: startRow.rowPinned,
          },
          endRow: { rowIndex: maxRowIndex, rowPinned: endRow.rowPinned },
        })
      }
      // If the range doesn't contains deSelectedRowIndex
      else {
        result.push(range)
      }
    }
  }

  return result
}
